/**
 * Cart Drawer – AJAX cart for the 3 Zebras theme.
 *
 * Handles: open/close, AJAX add/change/remove, DOM re-render via
 * /cart.js + snippet re-fetch, free-shipping bar, cart-count badge,
 * focus trapping, body scroll-lock, and upsell quick-add.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
   * Helpers
   * ----------------------------------------------------------------*/
  const qs = (sel, root) => (root || document).querySelector(sel);
  const qsa = (sel, root) => [...(root || document).querySelectorAll(sel)];

  const ROUTES = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root
    : '/';

  /**
   * POST to a /cart/*.js endpoint. Unlike a naive helper, this NEVER throws on
   * a non-2xx HTTP status — Shopify returns a 422 with a JSON body describing
   * *why* (e.g. "You can only add 3 of X to the cart.") when a request exceeds
   * available inventory. We must read that body, not discard it. The promise
   * only rejects on a genuine network failure.
   *
   * Resolves to { ok, status, data } where `data` is the parsed JSON. Shopify
   * error payloads carry `status`, `message` and `description` fields.
   */
  function cartFetch(endpoint, body) {
    return fetch(`${ROUTES}cart/${endpoint}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) =>
      r
        .json()
        .then((data) => ({ ok: r.ok, status: r.status, data }))
        .catch(() => ({ ok: r.ok, status: r.status, data: {} }))
    );
  }

  // A cart response is an error when the HTTP status is non-OK or Shopify set an
  // error `status` on the body (add.js) or a top-level `errors` (change.js).
  function cartError(res) {
    if (!res || !res.ok) {
      return (res && res.data && (res.data.description || res.data.message)) || null;
    }
    if (res.data && res.data.status && res.data.description) {
      return res.data.description;
    }
    if (res.data && res.data.errors) {
      return typeof res.data.errors === 'string'
        ? res.data.errors
        : Object.values(res.data.errors).flat().join(' ');
    }
    return null;
  }

  function getCart() {
    return fetch(`${ROUTES}cart.js`, {
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());
  }

  /* ------------------------------------------------------------------
   * CartDrawer class
   * ----------------------------------------------------------------*/
  class CartDrawer {
    constructor() {
      this.drawer = qs('[data-cart-drawer]');
      if (!this.drawer) return;

      this.overlay = qs('[data-cart-drawer-overlay]', this.drawer);
      this.panel = qs('[data-cart-drawer-panel]', this.drawer);
      this.body = qs('[data-cart-drawer-body]', this.drawer);
      this.liveRegion = qs('#cart-drawer-live-region');
      this._focusableCache = null;
      this._previousFocus = null;
      this._boundKeyDown = this._onKeyDown.bind(this);
      this._abortController = null;

      this._bindStaticEvents();
      this._bindDynamicEvents();
      this._interceptForms();
    }

    /* ---- Open / Close ---- */

    open() {
      this._previousFocus = document.activeElement;
      this.drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._boundKeyDown);

      // Focus the close button after transition
      requestAnimationFrame(() => {
        const closeBtn = qs('[data-cart-drawer-close]', this.drawer);
        if (closeBtn) closeBtn.focus();
      });
    }

    close() {
      this.drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._boundKeyDown);

      if (this._previousFocus && typeof this._previousFocus.focus === 'function') {
        this._previousFocus.focus();
      }
      this._previousFocus = null;
    }

    get isOpen() {
      return this.drawer.classList.contains('is-open');
    }

    /* ---- Cart operations ---- */

    addToCart(variantId, quantity = 1, errorTarget) {
      this._setLoading(true);
      return cartFetch('add', {
        items: [{ id: variantId, quantity }],
      })
        .then((res) => {
          const message = cartError(res);
          if (message) {
            // Inventory limit (or other) — surface it instead of silently failing.
            this._showError(message, errorTarget);
            this._setLoading(false);
            return;
          }
          return this._refresh().then(() => this.open());
        })
        .catch((err) => {
          console.error('Add to cart error:', err);
          this._showError(this._genericError(), errorTarget);
          this._setLoading(false);
        });
    }

    changeQuantity(lineKey, quantity) {
      const item = qs(`[data-line-key="${lineKey}"]`, this.drawer);
      if (item) item.classList.add('is-updating');

      return cartFetch('change', { id: lineKey, quantity })
        .then((res) => {
          const message = cartError(res);
          if (message) {
            this._showError(message);
          } else if (res.ok && res.data && Array.isArray(res.data.items) && quantity > 0) {
            // /cart/change.js often returns 200 while silently CLAMPING the
            // quantity to available stock (no `errors` field). Detect that by
            // comparing the requested quantity to what actually landed, so the
            // "+" button no longer appears to do nothing at the stock ceiling.
            const line = res.data.items.find((i) => i.key === lineKey);
            if (line && line.quantity < quantity) {
              this._showError(this._maxInventoryError());
            }
          }
          return this._refresh();
        })
        .catch((err) => {
          console.error('Change quantity error:', err);
          this._showError(this._genericError());
          if (item) item.classList.remove('is-updating');
        });
    }

    removeItem(lineKey) {
      return this.changeQuantity(lineKey, 0);
    }

    /* ---- Re-render drawer contents ---- */

    _refresh() {
      return getCart().then((cart) => {
        this._updateCartCount(cart.item_count);
        this._renderDrawerContents(cart);
        this._announce(cart);
        this._setLoading(false);
      });
    }

    /**
     * Fetch the cart-drawer snippet HTML by requesting the page with
     * section_id. Since the drawer is rendered in theme.liquid, we
     * fetch /?section_id=cart-drawer. However, snippets cannot be
     * fetched as sections. Instead, we re-build the inner HTML from
     * the cart JSON and a full-page fetch approach.
     *
     * For reliability, we fetch the current page with a special
     * query param, extract the cart-drawer HTML, and swap it in.
     */
    _renderDrawerContents(cart) {
      // Strategy: Fetch the homepage (lightweight) with a cache-bust
      // and extract the cart-drawer HTML from the response.
      // We append ?sections=cart-drawer-snippet but since it's a snippet
      // not a section, we use a simpler full-page fetch approach.

      const url = `${ROUTES}?cart_drawer_refresh=1&t=${Date.now()}`;

      fetch(url, { headers: { Accept: 'text/html' } })
        .then((r) => r.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newDrawer = doc.querySelector('[data-cart-drawer]');
          if (newDrawer) {
            // Preserve open state
            const wasOpen = this.isOpen;
            const panel = qs('[data-cart-drawer-panel]', newDrawer);

            // Replace panel content
            if (panel && this.panel) {
              this.panel.innerHTML = panel.innerHTML;
            }

            // Re-cache body reference
            this.body = qs('[data-cart-drawer-body]', this.drawer);

            // Re-bind dynamic events
            this._bindDynamicEvents();

            // Load API recommendations if configured
            this._loadApiRecommendations();

            // Maintain focus inside drawer if open
            if (wasOpen) {
              const closeBtn = qs('[data-cart-drawer-close]', this.drawer);
              if (closeBtn) closeBtn.focus();
            }
          }
        })
        .catch((err) => {
          console.error('Drawer refresh error:', err);
        });
    }

    _loadApiRecommendations() {
      const upsellEl = qs('[data-cart-drawer-upsell]', this.drawer);
      if (!upsellEl) return;

      const source = upsellEl.dataset.upsellSource;
      if (!source || source === 'collection') return;

      const productsContainer = qs('.cart-drawer__upsell-products', upsellEl);
      const hasCollectionItems = productsContainer && productsContainer.children.length > 0;

      // "collection_api": only fetch if collection had no items
      if (source === 'collection_api' && hasCollectionItems) return;

      // Get a product ID from cart to base recommendations on
      const cartItems = qsa('[data-cart-drawer-item]', this.drawer);
      if (cartItems.length === 0) return;
      const productId = cartItems[0].dataset.productId;
      if (!productId) return;

      // Get product IDs already in cart (to filter them out)
      const cartProductIds = new Set(cartItems.map((el) => el.dataset.productId));

      const baseUrl = upsellEl.dataset.recommendationsUrl;
      const sectionId = upsellEl.dataset.sectionId;
      if (!baseUrl || !sectionId) return;

      const url = `${baseUrl}?product_id=${productId}&limit=6&intent=complementary&section_id=${sectionId}`;

      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const items = [...doc.querySelectorAll('.cart-drawer__upsell-item')];

          // Filter out products already in cart, take max 3
          const filtered = items
            .filter((el) => !cartProductIds.has(el.dataset.productId))
            .slice(0, 3);

          if (filtered.length === 0) return;

          // Replace or populate the container
          if (source === 'api_collection' || !hasCollectionItems) {
            productsContainer.innerHTML = '';
            filtered.forEach((el) => productsContainer.appendChild(el));
            this._bindDynamicEvents();
          }
        })
        .catch((err) => console.error('Recommendations error:', err));
    }

    _updateCartCount(count) {
      // Update all cart count badges in the header
      const badges = qsa('.header__cart-count');
      badges.forEach((badge) => {
        badge.textContent = count;
      });

      // If count is 0, remove badges; if > 0 ensure they exist
      const cartLinks = qsa('.header__cart, [data-cart-drawer-toggle]');
      cartLinks.forEach((link) => {
        let badge = qs('.header__cart-count', link);
        if (count > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'header__cart-count';
            link.appendChild(badge);
          }
          badge.textContent = count;
        } else if (badge) {
          badge.remove();
        }
      });
    }

    _announce(cart) {
      if (!this.liveRegion) return;
      if (cart.item_count === 0) {
        this.liveRegion.textContent = this.liveRegion.dataset.emptyText || '';
      } else {
        // Wording comes from the theme's translations (cart-drawer.liquid), so it follows the page language.
        const template = this.liveRegion.dataset.countText || '[count] · [total]';
        this.liveRegion.textContent = template
          .replace('[count]', cart.item_count)
          .replace('[total]', this._formatMoney(cart.total_price));
      }
    }

    _formatMoney(cents) {
      // Formats in the page language and the active currency ("12,50 €" in pt/fr/de, "€12.50" in en).
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(cents / 100);
      } catch (error) {
        return `${(cents / 100).toFixed(2)} ${currency}`;
      }
    }

    _setLoading(loading) {
      if (this.body) {
        this.body.classList.toggle('is-loading', loading);
      }
    }

    /* ---- Error surfacing ---- */

    _genericError() {
      return (this.drawer && this.drawer.dataset.errorText) || 'Something went wrong. Please try again.';
    }

    _maxInventoryError() {
      return (this.drawer && this.drawer.dataset.maxInventoryText) || this._genericError();
    }

    /**
     * Show a cart error to the shopper. When the failure originates from a
     * product form (`errorTarget` is that <form>), render the message inline
     * next to its Add-to-cart button. Otherwise show it inside the drawer.
     * Always mirror it to the live region for assistive tech.
     */
    _showError(message, errorTarget) {
      if (!message) return;

      if (this.liveRegion) this.liveRegion.textContent = message;

      const form = errorTarget instanceof HTMLElement
        ? (errorTarget.matches('form') ? errorTarget : errorTarget.closest('form'))
        : null;

      if (form) {
        let box = qs('[data-product-form-error]', form);
        if (!box) {
          box = document.createElement('div');
          box.className = 'product-form__error';
          box.setAttribute('data-product-form-error', '');
          box.setAttribute('role', 'alert');
          box.setAttribute('aria-live', 'assertive');
          form.appendChild(box);
        }
        box.textContent = message;
        box.hidden = false;
        return;
      }

      // Drawer-scoped notice at the top of the body.
      if (!this.body) return;
      let notice = qs('[data-cart-drawer-error]', this.drawer);
      if (!notice) {
        notice = document.createElement('div');
        notice.className = 'cart-drawer__error';
        notice.setAttribute('data-cart-drawer-error', '');
        notice.setAttribute('role', 'alert');
        this.body.insertBefore(notice, this.body.firstChild);
      }
      notice.textContent = message;
      notice.hidden = false;
      if (this._errorTimer) clearTimeout(this._errorTimer);
      this._errorTimer = setTimeout(() => {
        if (notice) notice.hidden = true;
      }, 5000);
    }

    /* ---- Focus trap ---- */

    _onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }

      if (e.key === 'Tab') {
        this._trapFocus(e);
      }
    }

    _trapFocus(e) {
      const focusable = qsa(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        this.panel
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    /* ---- Event binding ---- */

    _bindStaticEvents() {
      // Overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Cart icon toggle buttons
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-cart-drawer-toggle]');
        if (toggle) {
          e.preventDefault();
          if (this.isOpen) {
            this.close();
          } else {
            this.open();
          }
        }
      });
    }

    _bindDynamicEvents() {
      // Close button
      qsa('[data-cart-drawer-close]', this.drawer).forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });

      // Quantity minus
      qsa('[data-cart-drawer-qty-minus]', this.drawer).forEach((btn) => {
        btn.addEventListener('click', () => {
          const item = btn.closest('[data-cart-drawer-item]');
          if (!item) return;
          const key = item.dataset.lineKey;
          const valEl = qs('[data-cart-drawer-qty-value]', item);
          const currentQty = parseInt(valEl.textContent, 10) || 1;
          const newQty = Math.max(0, currentQty - 1);
          this.changeQuantity(key, newQty);
        });
      });

      // Quantity plus
      qsa('[data-cart-drawer-qty-plus]', this.drawer).forEach((btn) => {
        btn.addEventListener('click', () => {
          const item = btn.closest('[data-cart-drawer-item]');
          if (!item) return;
          const key = item.dataset.lineKey;
          const valEl = qs('[data-cart-drawer-qty-value]', item);
          const currentQty = parseInt(valEl.textContent, 10) || 1;
          const max = parseInt(item.dataset.inventoryMax, 10);
          if (!isNaN(max) && currentQty >= max) {
            // At the stock ceiling — don't fire a no-op request; explain why.
            this._showError(this._maxInventoryError());
            return;
          }
          this.changeQuantity(key, currentQty + 1);
        });
      });

      // Reflect the stock ceiling on the +/- buttons (disable, don't silently no-op).
      this._updateQtyButtonStates();

      // Remove buttons
      qsa('[data-cart-drawer-remove]', this.drawer).forEach((btn) => {
        btn.addEventListener('click', () => {
          const item = btn.closest('[data-cart-drawer-item]');
          if (!item) return;
          this.removeItem(item.dataset.lineKey);
        });
      });

      // Upsell add buttons
      qsa('[data-cart-drawer-upsell-add]', this.drawer).forEach((btn) => {
        btn.addEventListener('click', () => {
          const variantId = parseInt(btn.dataset.variantId, 10);
          if (!variantId) return;
          btn.disabled = true;
          this.addToCart(variantId, 1);
        });
      });
    }

    // Disable "+" at a line's stock ceiling. "-" stays enabled at quantity 1 —
    // there it acts as Remove (the handler drops the line to 0). A disabled "+"
    // is a clearer signal than a click that does nothing (mirrors Horizon/Colorblock).
    _updateQtyButtonStates() {
      qsa('[data-cart-drawer-item]', this.drawer).forEach((item) => {
        const valEl = qs('[data-cart-drawer-qty-value]', item);
        if (!valEl) return;
        const qty = parseInt(valEl.textContent, 10) || 1;
        const max = parseInt(item.dataset.inventoryMax, 10);
        const plusBtn = qs('[data-cart-drawer-qty-plus]', item);
        if (plusBtn) plusBtn.disabled = !isNaN(max) && qty >= max;
      });
    }

    /* ---- Intercept product forms ---- */

    _interceptForms() {
      document.addEventListener('submit', (e) => {
        // Only intercept product forms when cart type is drawer
        const form = e.target;
        if (
          form.action &&
          form.action.includes('/cart/add') &&
          form.method.toLowerCase() === 'post'
        ) {
          e.preventDefault();
          const formData = new FormData(form);
          const id = formData.get('id');
          const quantity = parseInt(formData.get('quantity'), 10) || 1;

          // Clear any prior error before retrying.
          const prevError = qs('[data-product-form-error]', form);
          if (prevError) prevError.hidden = true;

          if (id) {
            // Disable submit button during request
            const submitBtn = qs('[type="submit"], button:not([type="button"])', form);
            if (submitBtn) submitBtn.disabled = true;

            this.addToCart(parseInt(id, 10), quantity, form).finally(() => {
              if (submitBtn) submitBtn.disabled = false;
            });
          }
        }
      }, true);
    }
  }

  /* ------------------------------------------------------------------
   * Initialize
   * ----------------------------------------------------------------*/
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.cartDrawer = new CartDrawer();
    });
  } else {
    window.cartDrawer = new CartDrawer();
  }
})();
